/**
 * Datumsfeld mit Entwurf (P5-Review 27.10.2026): das Feld behält, was getippt wird, und meldet nur GÜLTIGE Tage nach
 * außen (`onGueltig`). Vorher hing es direkt an der URL — beim Tippen der Jahreszahl entstanden „0002-…", „0020-…",
 * „0202-…", und jede Zwischenstufe löste eine Abfrage über die ganze Historie aus. Mit Untergrenze, aber ohne Entwurf
 * setzte React das Feld nach der ersten Ziffer zurück, und ein Jahr ließ sich gar nicht mehr tippen.
 * Benutzt von /auswertungen/bestellungen und /auswertungen/werkzeuge.
 */
import { FC, useEffect, useRef, useState } from 'react';
import { TextField } from '@mui/material';
import moment from 'moment';

const DATUM_RE = /^\d{4}-\d{2}-\d{2}$/;
/** Frühester wählbarer Tag (wie `bestellungenService.BESTELLUNGEN_FRUEHESTENS` im Backend). */
export const DATUM_FRUEHESTENS = '2020-01-01';

/** `YYYY-MM-DD`, ein echter Kalendertag, nicht vor `DATUM_FRUEHESTENS`. */
export const datumGueltig = (v: string | null | undefined): v is string =>
  !!v && DATUM_RE.test(v) && v >= DATUM_FRUEHESTENS && moment(v, 'YYYY-MM-DD', true).isValid();

const DatumFeld: FC<{
  label: string;
  value: string;
  min?: string;
  max?: string;
  width?: number;
  /** Nur mit einem gültigen Tag innerhalb von min/max aufgerufen. */
  onGueltig: (v: string) => void;
}> = ({ label, value, min = DATUM_FRUEHESTENS, max, width = 160, onGueltig }) => {
  const [entwurf, setEntwurf] = useState(value);
  // Enter auf einem ungültigen Tag: sagen, warum nichts passiert (vorher stand der Wert still da, Bedienprüfung 28.10.).
  const [abgelehnt, setAbgelehnt] = useState(false);
  const tag = (v: string) => moment(v, 'YYYY-MM-DD').format('DD.MM.YYYY');
  // Neuer Wert von außen (Schnellwahl, Zurück im Browser): ins Feld übernehmen.
  useEffect(() => setEntwurf(value), [value]);
  // Gültige Tage erst nach kurzer Ruhe melden (oder sofort bei Enter/Verlassen): beim Tippen von „25.10.2026" sind
  // 02.10., 25.10., 25.01. Zwischenstufen — jede löste sonst eine Abfrage und einen Eintrag im Browser-Verlauf aus.
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const passt = (v: string) => datumGueltig(v) && v >= min && (!max || v <= max);
  const melde = (v: string) => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = null;
    if (passt(v) && v !== value) onGueltig(v);
  };
  useEffect(() => () => {
    if (timer.current) clearTimeout(timer.current);
  }, []);
  return (
    <TextField
      label={label}
      type="date"
      size="small"
      value={entwurf}
      inputProps={{ min, max }}
      InputLabelProps={{ shrink: true }}
      onChange={(e) => {
        const v = e.target.value;
        setEntwurf(v);
        setAbgelehnt(false);
        if (timer.current) clearTimeout(timer.current);
        timer.current = passt(v) ? setTimeout(() => melde(v), 600) : null;
      }}
      onKeyDown={(e) => {
        if (e.key !== 'Enter') return;
        if (passt(entwurf)) melde(entwurf);
        else setAbgelehnt(true);
      }}
      // Beim Verlassen: gültige Eingabe sofort übernehmen, unfertige verwerfen (das Feld zeigt wieder den geltenden Tag).
      onBlur={() => {
        if (passt(entwurf)) melde(entwurf);
        else setEntwurf(value);
        setAbgelehnt(false);
      }}
      error={abgelehnt}
      helperText={abgelehnt ? `Tag zwischen ${tag(min)} und ${max ? tag(max) : 'heute'}` : undefined}
      sx={{ width }}
    />
  );
};

export default DatumFeld;
